import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Row, Form, Card } from "react-bootstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { getSalaries, taxInputs } from "../../../redux/slices/salariesSlice";
import { ReactComponent as FiSliders } from "../../../assets/images/filters-icon.svg";
import usePlan from "../../../hooks/usePlan";
import {
  activityEnd,
  activityStart,
} from "../../../redux/slices/trackActivitySlice";
import usePageTracking from "../../../hooks/usePageTracking";
import { calculatorAnalytics } from "../../../redux/slices/lifeinsuranceSlice";
import { sessionId } from "../../../redux/slices/cityComparisonSlice";
import PaywallSheet from "../../../components/PaywallSheet";

const TaxPage = ({ data }) => {
  const dispatch = useDispatch();
  const salaryInputsData = useSelector((state) => state.salaries);
  const user = useSelector((state) => state.auth?.user);

  // True for any paid plan (Plus grandfathered, Pro, Pro Trial, Pro+Newsletter).
  const { slug: userPlanSlug, isPaid: isProUser, isFree: isFreeUser } = usePlan();
  
  // Debug logs
  console.log("🔍 Tax Page - User Plan Debug:", {
    userPlanSlug,
    isProUser,
    isFreeUser,
    userId: user?.id,
  });
  
  // Track filter changes for free users - store which filters have been changed
  const [changedFilters, setChangedFilters] = useState(() => {
    if (!isFreeUser || !user?.id) return {};
    const stored = localStorage.getItem(`tax_filters_changed_${user?.id}`);
    const parsed = stored ? JSON.parse(stored) : {};
    console.log("📦 Tax Page - LocalStorage check:", {
      key: `tax_filters_changed_${user?.id}`,
      value: stored,
      parsed: parsed
    });
    return parsed;
  });

  // Subscription modal state
  const [showPaywallSheet, setShowPaywallSheet] = useState(false);
  const [upgradeSource, setUpgradeSource] = useState("");

  const salaryFilters = salaryInputsData.salariesInputData;

  const initialSelected = [
    // "401k",
    // "Vision Insurance",
    // "Long Term Disability Insurance",
    // "FSA",
  ];

  const defaultDeductionConfig = useMemo(
    () => ({
      "401k": { type: "Fixed Amount", value: 950 },
      "Medical Insurance": { type: "Percentage", value: 0 },
      "Dental Coverage": { type: "Percentage", value: 0 },
      "Vision Insurance": { type: "Percentage", value: 10 },
      "Long Term Disability Insurance": { type: "Percentage", value: 20 },
      "Life Insurance": { type: "Percentage", value: 0 },
      "Commuter Plan": { type: "Percentage", value: 0 },
      HSA: { type: "Percentage", value: 0 },
      FSA: { type: "Fixed Amount", value: 208 },
    }),
    [],
  );

  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef(null);
  const [salariesData, setSalariesData] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [selectedDeductions, setSelectedDeductions] = useState(initialSelected);

  const [payBy, setPayBy] = useState("Month");
  const [deductionsConfig, setDeductionsConfig] = useState(() =>
    initialSelected.map((name) => ({
      name,
      ...(defaultDeductionConfig[name] || { type: "fixed", value: 0 }),
    })),
  );

  const [filters, setFilters] = useState({
    page_type: "tax",
    page: 1,
    length: 10,
    years_of_experience: salaryFilters.years_of_experience || 5,
    position: salaryFilters.position || "Night Shift",
    hours_per_week: salaryFilters.hours_per_week || 40,
    over_time: salaryFilters.over_time || 0,
    double_time: salaryFilters.double_time || 0,
    rate_per: salaryFilters.rate_per || payBy || "Month",
    pay_by: salaryFilters.rate_per || "Month",
    filing_status: "Married filing jointly",
    additional_withholdings: "Federal",
    additional_withholding_amount: 0,
    selected_deductions: initialSelected,
    deductions: deductionsConfig.reduce((acc, d) => {
      acc[d.name] = {
        type: d.type,
        value: d.value,
        ficaExempt: d.ficaExempt || false,
      };
      return acc;
    }, {}),
  });

  const [view, setView] = useState("table");

  useEffect(() => {
    dispatch(taxInputs(filters));
  }, []);

  const chartData = React.useMemo(() => {
    if (salariesData.length === 0) {
      return { data: [], maxSalary: 1, niceMax: 20000 };
    }

    const grossPays = salariesData.map((s) => s.grossPay || 0);
    const maxSalary = Math.max(...grossPays);
    const niceMax = maxSalary > 0 ? Math.ceil(maxSalary / 5000) * 5000 : 20000;

    return {
      data: salariesData,
      maxSalary,
      niceMax,
    };
  }, [salariesData]);

  const [shouldRefetch, setShouldRefetch] = useState(false);
  const isFirstFilterSync = useRef(true);
  const isFirstDeductionSync = useRef(true);

  // Track Activity
  usePageTracking("TAX_CALCULATOR");

  useEffect(() => {
    const payload = {
      page_name: "TAX_CALCULATOR",
      platform: "web",
    };

    dispatch(activityStart(payload));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(
        activityEnd({
          page_name: "TAX_CALCULATOR",
        }),
      );
    };
  }, [dispatch]);

  

  const formatInputValue = (value) => {
    if (typeof value !== "string") return value;

    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^\w]/g, "_");
  };

  // const handleToggleDeduction = (item) => {
  //   const session_id = sessionStorage.getItem("comparison_session_id");

  //   setSelectedDeductions((prev) => {
  //     const isSelected = prev.includes(item);
  //     const formattedValue = formatInputValue(item);

  //     // 🔥 Fire analytics on toggle (check or uncheck)
  //     if (session_id) {
  //       dispatch(
  //         calculatorAnalytics({
  //           calculator_type: "tax",
  //           event_type: "input_adjusted",
  //           input_name: "pre_tax_deduction",
  //           inputs: formattedValue,
  //           session_id,
  //           timestamp: new Date().toISOString(),
  //         }),
  //       );
  //     }

  //     if (isSelected) {
  //       return prev.filter((i) => i !== item);
  //     } else {
  //       const newSelected = [...prev, item];

  //       setDeductionsConfig((current) => {
  //         const exists = current.some((d) => d.name === item);
  //         if (!exists) {
  //           const defaultConfig = defaultDeductionConfig[item] || {
  //             type: "Fixed Amount",
  //             value: 0,
  //           };
  //           return [...current, { name: item, ...defaultConfig }];
  //         }
  //         return current;
  //       });

  //       return newSelected;
  //     }
  //   });
  // };

  const buildDeductionInputs = useCallback(
    (selected, configOverride = null) => {
      const result = {};

      const configSource = configOverride || deductionsConfig;

      selected.forEach((name) => {
        const existing = configSource.find((d) => d.name === name);
        const config = existing || defaultDeductionConfig[name];

        if (config) {
          result[formatInputValue(name)] = {
            type: formatInputValue(config.type),
            value: config.value,
          };
        }
      });

      return result;
    },
    [deductionsConfig, defaultDeductionConfig],
  );
  const handleToggleDeduction = (item) => {
    const session_id = sessionStorage.getItem("comparison_session_id");

    setSelectedDeductions((prev) => {
      const isSelected = prev.includes(item);

      // 🔓 ALWAYS allow unchecking (deselecting)
      if (isSelected) {
        const newSelected = prev.filter((i) => i !== item);
        return newSelected;
      }

      // 🚫 RESTRICTION LOGIC FOR SELECTING (checking)
      
      // 1. Pro/Plus/Free-trial users: Unlimited access
      if (isProUser) {
        console.log("✅ Tax Page - Pro user - allowing checkbox selection");
        
        const newSelected = [...prev, item];
        const all_inputs = {
          deductions: newSelected.map((d) => formatInputValue(d)),
        };

        if (session_id) {
          dispatch(
            calculatorAnalytics({
              calculator_type: "tax",
              event_type: "input_adjusted",
              input_name: "pre_tax_deduction",
              results_viewed: true,
              session_id,
              timestamp: new Date().toISOString(),
              all_inputs,
            }),
          );
        }

        return newSelected;
      }

      // 2. Free users: Can select only ONE checkbox total
      if (isFreeUser && user?.id) {
        if (changedFilters["pre_tax_deduction"] === true) {
          // Already selected one checkbox before - show modal
          console.log(`❌ Tax Page - Free user already selected a checkbox - showing modal`);
          
          if (session_id) {
            dispatch(
              calculatorAnalytics({
                calculator_type: "tax",
                event_type: "calculator_limit_hit",
                input_name: "pre_tax_deduction",
                inputs: formatInputValue(item),
                results_viewed: true,
                session_id,
                timestamp: new Date().toISOString(),
              }),
            );
          }
          
          setUpgradeSource("tax_page_free_limit");
          setShowPaywallSheet(true);
          return prev; // Don't change selection
        } else {
          // First checkbox selection - allow it and mark as changed
          console.log(`✅ Tax Page - Free user first checkbox selection - allowing and marking as changed`);
          
          const newSelected = [...prev, item];
          const all_inputs = {
            deductions: newSelected.map((d) => formatInputValue(d)),
          };

          if (session_id) {
            dispatch(
              calculatorAnalytics({
                calculator_type: "tax",
                event_type: "input_adjusted",
                input_name: "pre_tax_deduction",
                results_viewed: true,
                session_id,
                timestamp: new Date().toISOString(),
                all_inputs,
              }),
            );
          }

          // Mark pre_tax_deduction as changed in localStorage
          const newChangedFilters = {
            ...changedFilters,
            pre_tax_deduction: true
          };
          const storageKey = `tax_filters_changed_${user?.id}`;
          localStorage.setItem(storageKey, JSON.stringify(newChangedFilters));
          setChangedFilters(newChangedFilters);
          
          console.log("💾 Tax Page - Saved pre_tax_deduction to localStorage:", {
            key: storageKey,
            value: newChangedFilters,
            verified: localStorage.getItem(storageKey)
          });

          return newSelected;
        }
      }

      // 3. No plan or other plans: Block immediately
      console.log("❌ Tax Page - No plan or unknown plan - showing modal");
      
      if (session_id) {
        dispatch(
          calculatorAnalytics({
            calculator_type: "tax",
            event_type: "calculator_limit_hit",
            input_name: "pre_tax_deduction",
            inputs: formatInputValue(item),
            results_viewed: true,
            session_id,
            timestamp: new Date().toISOString(),
          }),
        );
      }
      
      setUpgradeSource("tax_page");
      setShowPaywallSheet(true);
      return prev; // Don't change selection
    });
  };
  const handleFilterChange = (field, value) => {
    console.log("🎯 Tax Page - Filter Change Attempt:", {
      field,
      value,
      isProUser,
      isFreeUser,
      changedFilters,
      userPlanSlug,
      user: user,
    });

    const session_id = sessionStorage.getItem("comparison_session_id");

    const formattedValue = formatInputValue(value);

    // 🚫 RESTRICTION LOGIC
    
    // 1. Pro/Plus/Free-trial users: Unlimited access
    if (isProUser) {
      console.log("✅ Tax Page - Pro user - allowing change");
      
      if (session_id) {
        dispatch(
          calculatorAnalytics({
            calculator_type: "tax",
            event_type: "input_adjusted",
            input_name: field,
            inputs: formattedValue,
            results_viewed: true,
            session_id,
            timestamp: new Date().toISOString(),
          }),
        );
      }

      setFilters((prev) => ({
        ...prev,
        [field]: value,
        ...(field === "pay_by" && { rate_per: value }),
        page: 1,
      }));
      return;
    }

    // 2. Free users: Can change each filter once
    if (isFreeUser && user?.id) {
      if (changedFilters[field] === true) {
        // This filter was already changed once - show modal
        console.log(`❌ Tax Page - Free user already changed "${field}" - showing modal`);
        
        if (session_id) {
          dispatch(
            calculatorAnalytics({
              calculator_type: "tax",
              event_type: "calculator_limit_hit",
              input_name: field,
              inputs: formattedValue,
              results_viewed: true,
              session_id,
              timestamp: new Date().toISOString(),
            }),
          );
        }
        
        setUpgradeSource("tax_page_free_limit");
        setShowPaywallSheet(true);
        return;
      } else {
        // First time changing this filter - allow it and mark as changed
        console.log(`✅ Tax Page - Free user first change for "${field}" - allowing and marking as changed`);
        
        if (session_id) {
          dispatch(
            calculatorAnalytics({
              calculator_type: "tax",
              event_type: "input_adjusted",
              input_name: field,
              inputs: formattedValue,
              results_viewed: true,
              session_id,
              timestamp: new Date().toISOString(),
            }),
          );
        }

        setFilters((prev) => ({
          ...prev,
          [field]: value,
          ...(field === "pay_by" && { rate_per: value }),
          page: 1,
        }));
        
        // Mark this filter as changed in localStorage
        const newChangedFilters = {
          ...changedFilters,
          [field]: true
        };
        const storageKey = `tax_filters_changed_${user?.id}`;
        localStorage.setItem(storageKey, JSON.stringify(newChangedFilters));
        setChangedFilters(newChangedFilters);
        
        console.log("💾 Tax Page - Saved to localStorage:", {
          key: storageKey,
          value: newChangedFilters,
          verified: localStorage.getItem(storageKey)
        });
        return;
      }
    }

    // 3. No plan or other plans: Block immediately
    console.log("❌ Tax Page - No plan or unknown plan - showing modal");
    
    if (session_id) {
      dispatch(
        calculatorAnalytics({
          calculator_type: "tax",
          event_type: "calculator_limit_hit",
          input_name: field,
          inputs: formattedValue,
          results_viewed: true,
          session_id,
          timestamp: new Date().toISOString(),
        }),
      );
    }
    
    setUpgradeSource("tax_page");
    setShowPaywallSheet(true);
  };

  // Track initial value for input fields to detect actual changes
  const initialInputValueRef = useRef({});

  // Handler for when input field gains focus - check restriction here
  const handleInputFocus = (e, field, value) => {
    console.log(`📝 Input focus on ${field}:`, {
      value,
      alreadyChanged: changedFilters[field],
      isProUser,
      isFreeUser
    });

    // Pro users can always edit
    if (isProUser) {
      initialInputValueRef.current[field] = value;
      return;
    }

    // Free users: Check if they already changed this field
    if (isFreeUser && user?.id) {
      if (changedFilters[field] === true) {
        // Already changed once - show modal immediately and blur the input
        console.log(`❌ Tax Page - Free user already changed "${field}" - showing modal on focus`);
        
        // Blur the input to prevent modal from reopening
        if (e && e.target) {
          e.target.blur();
        }
        
        setUpgradeSource("tax_page_free_limit");
        setShowPaywallSheet(true);
        return;
      } else {
        // First time - allow editing
        initialInputValueRef.current[field] = value;
        return;
      }
    }

    // No plan - block and blur
    if (e && e.target) {
      e.target.blur();
    }
    setUpgradeSource("tax_page");
    setShowPaywallSheet(true);
  };

  // Separate handler for input fields (Amount) - allows typing without restriction
  const handleInputChange = (field, value) => {
    // Only update if not blocked (Pro or Free first time)
    if (isProUser || (isFreeUser && !changedFilters[field])) {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
        page: 1,
      }));
    }
  };

  // Handler for when input field loses focus (blur) - mark as changed if value changed
  const handleInputBlur = (field, value) => {
    const initialValue = initialInputValueRef.current[field];
    console.log(`🔍 Checking blur for ${field}:`, {
      initialValue,
      currentValue: value,
      changed: initialValue !== value,
      alreadyMarked: changedFilters[field]
    });

    // Only mark as changed if:
    // 1. Value actually changed from initial
    // 2. User is free user
    // 3. Not already marked as changed
    // 4. Value is not the default (0 for amount field)
    const isDefaultValue = (field === "additional_withholding_amount" && value === 0);
    
    if (isFreeUser && user?.id && initialValue !== value && !changedFilters[field] && !isDefaultValue) {
      console.log(`✅ Tax Page - Marking "${field}" as changed after blur`);
      
      const newChangedFilters = {
        ...changedFilters,
        [field]: true
      };
      const storageKey = `tax_filters_changed_${user?.id}`;
      localStorage.setItem(storageKey, JSON.stringify(newChangedFilters));
      setChangedFilters(newChangedFilters);
      
      console.log("💾 Tax Page - Saved to localStorage:", {
        key: storageKey,
        value: newChangedFilters,
        verified: localStorage.getItem(storageKey)
      });

      // Also trigger analytics
      const session_id = sessionStorage.getItem("comparison_session_id");
      if (session_id) {
        dispatch(
          calculatorAnalytics({
            calculator_type: "tax",
            event_type: "input_adjusted",
            input_name: field,
            inputs: value,
            results_viewed: true,
            session_id,
            timestamp: new Date().toISOString(),
          }),
        );
      }
    } else if (isDefaultValue) {
      console.log(`⚠️ Tax Page - Not marking "${field}" as changed because value is default (0)`);
    }
  };
  const handleTypeChange = (name, selectedOption) => {
    setDeductionsConfig((prev) =>
      prev.map((d) =>
        d.name === name ? { ...d, type: selectedOption.value } : d,
      ),
    );
  };

  //  const handleTypeChange = (name, selectedOption) => {
  //   const newType = selectedOption.value;

  //   setDeductionsConfig((prev) => {
  //     const updated = prev.map((d) =>
  //       d.name === name ? { ...d, type: newType } : d
  //     );

  //     const session_id = sessionStorage.getItem("comparison_session_id");

  //     if (session_id) {
  //       // clear previous debounce
  //       if (debounceRef.current) {
  //         clearTimeout(debounceRef.current);
  //       }

  //       debounceRef.current = setTimeout(() => {
  //         dispatch(
  //           calculatorAnalytics({
  //             calculator_type: "tax",
  //             event_type: "input_adjusted",
  //             input_name: "pre_tax_deduction",
  //             // inputs: formatInputValue(name),
  //             session_id,
  //             timestamp: new Date().toISOString(),
  //             all_inputs: buildDeductionInputs(selectedDeductions, updated),
  //           })
  //         );
  //       }, 1000); // 1 second debounce
  //     }

  //     return updated;
  //   });
  // };

  const handleValueChange = (name, newValue) => {
    setDeductionsConfig((prev) =>
      prev.map((d) =>
        d.name === name ? { ...d, value: Number(newValue) || 0 } : d,
      ),
    );
  };
  // const handleValueChange = (name, newValue) => {
  //   const value = Number(newValue) || 0;

  //   setDeductionsConfig((prev) => {
  //     const updated = prev.map((d) =>
  //       d.name === name ? { ...d, value } : d
  //     );

  //     const session_id = sessionStorage.getItem("comparison_session_id");

  //     if (session_id) {
  //       // clear previous timer
  //       if (debounceRef.current) {
  //         clearTimeout(debounceRef.current);
  //       }

  //       // start new debounce timer
  //       debounceRef.current = setTimeout(() => {
  //         dispatch(
  //           calculatorAnalytics({
  //             calculator_type: "tax",
  //             event_type: "input_adjusted",
  //             input_name: "pre_tax_deduction",
  //             // inputs: formatInputValue(name),
  //             session_id,
  //             timestamp: new Date().toISOString(),
  //             all_inputs: buildDeductionInputs(selectedDeductions, updated),
  //           })
  //         );
  //       }, 1000); // 1 second debounce
  //     }

  //     return updated;
  //   });
  // };
  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(Number(value))) {
      return "$0.00";
    }
    const num = Number(value);
    return `$${num
      .toFixed(2)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const formatCurrencyStrong = (value) => {
    if (value === undefined || value === null || isNaN(Number(value))) {
      return <strong className="text-success">$0.00</strong>;
    }
    const num = Number(value);
    return (
      <strong className="text-success">
        $
        {num
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      </strong>
    );
  };
  // const toPayBy = (m) => {
  //   if (!m) return 0;
  //   const val = Number(m);

  //   // FIX: Check for "Month", "Week", "Year" to match your options
  //   if (payBy === "Month") return val;
  //   if (payBy === "Year") return val * 12;
  //   if (payBy === "Bi-Weekly") return (val * 12) / 26;
  //   if (payBy === "Week") return (val * 12) / 52;

  //   return val; // Default return
  // };
  // NEW toPayBy Logic
  const toPayBy = (m) => {
    if (!m) return 0;
    // Since the API now responds with the correct rate (Weekly/Monthly)
    // based on our request, we simply return the value as-is.
    return Number(m);
  };

  const staticColumns = [
    {
      key: "name",
      header: "Hospital",
      className: "hospital-name fw-bold",
      render: (item) => item.hospital || "Unknown Hospital",
    },
    { key: "gross", header: "Gross" },
    { key: "federal", header: "Federal Tax" },
    { key: "state", header: "State Tax" },
    { key: "ss", header: "SS Tax" },
    { key: "medicare", header: "Medicare Tax" },
    { key: "sdi", header: "SDI" },
    { key: "net", header: "Net", className: "net" },
    { key: "postTax", header: "Post Tax" },
  ];

  const deductionColumnMap = {
    "401k": { deductionName: "401k", header: "401k" },
    "Medical Insurance": {
      deductionName: "Medical Insurance",
      header: "Medical Insurance",
    },
    "Dental Coverage": {
      deductionName: "Dental Coverage",
      header: "Dental Coverage",
    },
    "Vision Insurance": {
      deductionName: "Vision Insurance",
      header: "Vision Insurance",
    },
    "Long Term Disability Insurance": {
      deductionName: "Long Term Disability Insurance",
      header: "Long Term Disability",
    },
    "Life Insurance": {
      deductionName: "Life Insurance",
      header: "Life Insurance",
    },
    "Commuter Plan": {
      deductionName: "Commuter Plan",
      header: "Commuter Plan",
    },
    FSA: { deductionName: "FSA", header: "FSA" },
    HSA: { deductionName: "HSA", header: "HSA" },
  };

  const dynamicColumns = selectedDeductions
    .map((dedName) => deductionColumnMap[dedName])
    .filter(Boolean)
    .map((col) => ({
      key: `ded_${col.deductionName}`,
      header: col.header,
      deductionName: col.deductionName,
      className: "text-end fw-medium",
    }));

  const allColumns = [...staticColumns, ...dynamicColumns];

  useEffect(() => {
    if (isFirstDeductionSync.current) {
      isFirstDeductionSync.current = false;
      return;
    }

    setDeductionsConfig((prevConfig) => {
      const updatedConfig = selectedDeductions.map((name) => {
        const existing = prevConfig.find((d) => d.name === name);
        if (existing) return existing;
        return {
          name,
          ...(defaultDeductionConfig[name] || {
            type: "Fixed Amount",
            value: 0,
          }),
        };
      });
      return updatedConfig;
    });
    setShouldRefetch(true);
  }, [selectedDeductions, defaultDeductionConfig]);

  useEffect(() => {
    if (isFirstFilterSync.current) {
      isFirstFilterSync.current = false;
      return;
    }
    setShouldRefetch(true);
  }, [deductionsConfig]);

  useEffect(() => {
    if (!shouldRefetch) return;

    setFilters((prev) => ({
      ...prev,
      page: 1,
      selected_deductions: selectedDeductions,
      deductions: deductionsConfig.reduce((acc, d) => {
        if (selectedDeductions.includes(d.name)) {
          acc[d.name] = {
            type: d.type,
            value: d.value,
            ficaExempt: d.ficaExempt || false,
          };
        }
        return acc;
      }, {}),
    }));

    setShouldRefetch(false);
  }, [shouldRefetch, selectedDeductions, deductionsConfig]);

  const fetchSalaries = useCallback(async () => {
    try {
      if (filters.page === 1) {
        setIsInitialLoading(true);
      } else {
        setLoadingMore(true);
      }

      const res = await dispatch(getSalaries(filters));
      const docs = res?.payload?.data?.docs || [];
      const totalPages = res?.payload?.data?.pages || 1;

      if (filters.page === 1) {
        setSalariesData(docs);
      } else {
        setSalariesData((prev) => [...prev, ...docs]);
      }

      setHasMore(filters.page < totalPages);
    } catch (error) {
      console.error("Salaries Dispatch Error:", error);
    } finally {
      setIsInitialLoading(false);
      setLoadingMore(false);
    }
  }, [dispatch, filters]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  useEffect(() => {
    const handleScroll = () => {
      const bottomReached =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;

      if (bottomReached && hasMore && !loadingMore) {
        setFilters((prev) => ({
          ...prev,
          page: prev.page + 1,
        }));
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loadingMore]);

  const statusOptions = [
    { value: "Single", label: "Single" },
    { value: "Married filing jointly", label: "Married filing jointly" },
    { value: "Married filing separately", label: "Married filing separately" },
    { value: "Head of household", label: "Head of household" },
  ];

  const withholdingsOptions = [
    { value: "Federal", label: "Federal" },
    { value: "State", label: "State" },
    { value: "Both", label: "Both" },
  ];

  const deductionsList = [
    "401k",
    "Medical Insurance",
    "Dental Coverage",
    "Vision Insurance",
    "Long Term Disability Insurance",
    "Life Insurance",
    "Commuter Plan",
    "FSA",
    "HSA",
  ];

  const typeOptions = [
    { value: "Fixed Amount", label: "Fixed Amount" },
    { value: "Percentage", label: "Percentage" },
  ];

  const paybyOptions = [
    { value: "Month", label: "Monthly" },
    { value: "Bi-Weekly", label: "Bi-Weekly" },
    { value: "Week", label: "Weekly" },
    { value: "Year", label: "Yearly" },
  ];
  // const user = useSelector((state) => state.auth?.user);

  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem("comparison_session_id");
      sessionStorage.removeItem("session_expiry");

      console.log("🧹 Session cleared on logout");
    }
  }, [user]);
  
  // Sync changedFilters state when user changes
  useEffect(() => {
    console.log("🔄 Tax Page - Syncing changedFilters state:", {
      isFreeUser,
      userId: user?.id,
    });
    
    if (isFreeUser && user?.id) {
      const stored = localStorage.getItem(`tax_filters_changed_${user.id}`);
      const parsed = stored ? JSON.parse(stored) : {};
      console.log("📦 Tax Page - Reading from localStorage:", {
        key: `tax_filters_changed_${user.id}`,
        value: stored,
        parsed: parsed
      });
      setChangedFilters(parsed);
    } else {
      console.log("🔄 Tax Page - Not a free user or no user ID - resetting to empty object");
      setChangedFilters({});
    }
  }, [user?.id, isFreeUser]);
  useEffect(() => {
    if (!user?.id) return;

    const existingSession = sessionStorage.getItem("comparison_session_id");
    const expiry = sessionStorage.getItem("session_expiry");

    // 🔥 Check if valid session exists
    if (existingSession && expiry && Date.now() < Number(expiry)) {
      
      return;
    }

    // const token = localStorage.getItem("authToken");

    // dispatch(
    //   sessionId({
    //     user_id: user.id,
    //     access_token: token,
    //   }),
    dispatch(sessionId()).then((res) => {
      const data = res.payload?.data;

      if (data?.session_id) {
        sessionStorage.setItem("comparison_session_id", data.session_id);

        // 🔥 store expiry (important)
        const expiryTime = Date.now() + data.expires_in * 1000;
        sessionStorage.setItem("session_expiry", expiryTime);

        
      }
    });
  }, [user]);
  useEffect(() => {
    const session_id = sessionStorage.getItem("comparison_session_id");

    if (!session_id) return;

    dispatch(
      calculatorAnalytics({
        calculator_type: "tax",
        event_type: "tax_calculator_view",
        session_id,
        timestamp: new Date().toISOString(),
      }),
    );
  }, [dispatch]);

  const previousPayloadRef = useRef(null);

  useEffect(() => {
    const session_id = sessionStorage.getItem("comparison_session_id");

    if (!session_id) return;

    const formatInputValue = (value) => {
      if (typeof value !== "string") return value;
      return value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^\w]/g, "_");
    };

    const all_inputs = {
      position: formatInputValue(
        salaryInputsData?.salariesInputData?.position || filters.position,
      ),
      years_of_experience:
        salaryInputsData?.salariesInputData?.years_of_experience ||
        filters.years_of_experience,
      hours_per_week:
        salaryInputsData?.salariesInputData?.hours_per_week ||
        filters.hours_per_week,
      over_time:
        salaryInputsData?.salariesInputData?.over_time || filters.over_time,
      double_time:
        salaryInputsData?.salariesInputData?.double_time || filters.double_time,
      rate_per: formatInputValue(
        salaryInputsData?.salariesInputData?.rate_per || filters.pay_by,
      ),
    };

    const payloadString = JSON.stringify(all_inputs);

    // 🔥 Only fire if values actually changed
    if (previousPayloadRef.current === payloadString) return;

    previousPayloadRef.current = payloadString;

    dispatch(
      calculatorAnalytics({
        calculator_type: "tax",
        event_type: "input_adjusted",
        results_viewed: true,
        session_id,
        timestamp: new Date().toISOString(),
        all_inputs,
      }),
    );
  }, [
    filters.position,
    filters.years_of_experience,
    filters.hours_per_week,
    filters.over_time,
    filters.double_time,
    filters.pay_by,
    salaryInputsData?.salariesInputData?.position,
    salaryInputsData?.salariesInputData?.years_of_experience,
    salaryInputsData?.salariesInputData?.hours_per_week,
    salaryInputsData?.salariesInputData?.over_time,
    salaryInputsData?.salariesInputData?.double_time,
    salaryInputsData?.salariesInputData?.rate_per,
    dispatch
  ]);
  useEffect(() => {
    const session_id = sessionStorage.getItem("comparison_session_id");
    if (!session_id) return;

    const all_inputs = {
      position: formatInputValue(filters.position),
      years_of_experience: filters.years_of_experience,
      hours_per_week: filters.hours_per_week,
      over_time: filters.over_time,
      double_time: filters.double_time,
      rate_per: formatInputValue(filters.pay_by),

      filing_status: formatInputValue(filters.filing_status),
      additional_withholdings: formatInputValue(
        filters.additional_withholdings,
      ),
      additional_withholding_amount: filters.additional_withholding_amount,

      // deductions: buildDeductionInputs(selectedDeductions),
      deductions: selectedDeductions.map((d) => formatInputValue(d)),
    };

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      dispatch(
        calculatorAnalytics({
          calculator_type: "tax",
          event_type: "input_adjusted",
          results_viewed: true,
          session_id,
          timestamp: new Date().toISOString(),
          all_inputs,
        }),
      );
    }, 1000);
  }, [
    dispatch,
    filters.position,
    filters.years_of_experience,
    filters.hours_per_week,
    filters.over_time,
    filters.double_time,
    filters.pay_by,
    filters.filing_status,
    filters.additional_withholdings,
    filters.additional_withholding_amount,
    selectedDeductions,
    deductionsConfig,
    buildDeductionInputs,
  ]);
  return (
    <>
      <div className="filters-content-main">
        <Row>
          {/* Filter Sidebar */}
          <div className="filter-sidebar">
            <Card className="filter-container mb-0">
              <div className="inner-sub-sub-title">
                <div className="inner-sub-title">Tax Configuration</div>
                <button
                  className="filter-toggle-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {/* {showFilters ? "Hide Filters" : "Show Filters"} */}
                  <FiSliders />
                </button>
              </div>

              <div className={`filter ${showFilters ? "open" : "closed"}`}>
                <div className="sidebar-form">
                  <form>
                    <div className="tax-section-group">
                      <div className="tax-section">
                        <div className="form-group">
                          <label>Position: </label>
                          <span>
                            {salaryInputsData.salariesInputData.position ||
                              filters.position}
                          </span>
                        </div>
                      </div>
                      <div className="tax-section">
                        <div className="form-group">
                          <label>Years of Experience: </label>
                          <span>
                            {salaryInputsData.salariesInputData
                              .years_of_experience ||
                              filters.years_of_experience}
                          </span>
                        </div>
                      </div>
                      <div className="tax-section">
                        <div className="form-group">
                          <label>Hours/Week: </label>
                          <span>
                            {salaryInputsData.salariesInputData
                              .hours_per_week || filters.hours_per_week}
                          </span>
                        </div>
                      </div>
                      <div className="tax-section">
                        <div className="form-group">
                          <label>Overtime: </label>
                          <span>
                            {salaryInputsData.salariesInputData.over_time ||
                              filters.over_time}
                            /hours
                          </span>
                        </div>
                      </div>
                      <div className="tax-section">
                        <div className="form-group">
                          <label>Double Time: </label>
                          <span>
                            {salaryInputsData.salariesInputData.double_time ||
                              filters.double_time}
                            /hours
                          </span>
                        </div>
                      </div>
                      <div className="tax-section">
                        <div className="form-group">
                          <label>Rate Display: </label>
                          <span>
                            {salaryInputsData.salariesInputData.rate_per ||
                              filters.pay_by}
                          </span>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
                {/* Filing Status Filter */}
                <div className="filter-section">
                  <Form.Group className="form-group mb-0">
                    <Form.Label className="col-label">Filing Status</Form.Label>
                    <Select
                      value={{
                        value: filters.filing_status,
                        label: filters.filing_status,
                      }}
                      onChange={(option) =>
                        handleFilterChange("filing_status", option.value)
                      }
                      placeholder="Select Status"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={statusOptions}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "#fff",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
                          color: "#091F28",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "#F3F9FC",
                          },
                        }),
                      }}
                    />
                  </Form.Group>
                </div>

                {/* Additional Withholdings */}
                <div className="filter-section">
                  <Form.Group className="form-group mb-0">
                    <Form.Label className="col-label">
                      Additional Withholdings
                    </Form.Label>
                    <Select
                      value={{
                        value: filters.additional_withholdings,
                        label: filters.additional_withholdings,
                      }}
                      onChange={(option) =>
                        handleFilterChange(
                          "additional_withholdings",
                          option.value,
                        )
                      }
                      placeholder="Select Additional Withholdings"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={withholdingsOptions}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "#fff",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
                          color: "#091F28",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "#F3F9FC",
                          },
                        }),
                      }}
                    />
                  </Form.Group>
                </div>

                {/* Amount Input */}
                <div className="filter-section">
                  <Form.Group className="form-group mb-0">
                    <Form.Label htmlFor="amount" className="col-label">
                      Amount (Monthly)
                    </Form.Label>

                    <input
                      type="number"
                      id="amount"
                      className="col-input"
                      min="0"
                      value={filters.additional_withholding_amount}
                      onFocus={(e) => {
                        const value = Number(e.target.value);
                        handleInputFocus(e, "additional_withholding_amount", value);
                      }}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                          handleInputChange("additional_withholding_amount", value);
                        }
                      }}
                      onBlur={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                          handleInputBlur("additional_withholding_amount", value);
                        }
                      }}
                    />
                  </Form.Group>
                </div>

                {/*  Pre-Tax Deduction Section */}
                <div className="filter-section">
                  {/* <h6>Pre-Tax Deduction</h6> */}
                  <div className="inner-sub-sub-title">
                    <div className="inner-sub-title">Pre-Tax Deduction</div>
                  </div>
                  <div className="form-check-content">
                    {deductionsList.map((item) => (
                      <div key={item} className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedDeductions.includes(item)}
                          onChange={() => handleToggleDeduction(item)}
                        />
                        <label className="form-check-label">{item}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {deductionsConfig.length > 0 && (
                  <div className="filter-section">
                    {/* <h6>Configure Selected Deductions</h6> */}
                    <div className="inner-sub-sub-title">
                      <div className="inner-sub-title">
                        Configure Selected Deductions
                      </div>
                    </div>
                    <div className="deduction-list">
                      <div className="deduction-row">
                        {deductionsConfig.map((deduction, index) => (
                          <div key={deduction.name} className="deduction-item">
                            <label className="deduction-label">
                              {deduction.name}
                            </label>
                            <Select
                              options={typeOptions}
                              value={
                                typeOptions.find(
                                  (opt) => opt.value === deduction.type,
                                ) || typeOptions[0]
                              }
                              onChange={(selected) =>
                                handleTypeChange(deduction.name, selected)
                              }
                              className="react-select-container"
                              classNamePrefix="react-select"
                              menuPosition="fixed"
                              styles={{
                                menu: (base) => ({
                                  ...base,
                                  borderRadius: "15px",
                                  marginTop: "8px",
                                  backgroundColor: "#fff",
                                  padding: "6px",
                                  zIndex: "1000",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  borderRadius: "8px",
                                  padding: "10px",
                                  backgroundColor: state.isFocused
                                    ? "#F3F9FC"
                                    : "#fff",
                                  color: "#091F28",
                                  cursor: "pointer",
                                  "&:active": {
                                    backgroundColor: "#F3F9FC",
                                  },
                                  fontSize: "0.8rem",
                                }),
                              }}
                            />
                            <input
                              type="number"
                              className="value-input"
                              value={deduction.value}
                              onChange={(e) =>
                                handleValueChange(
                                  deduction.name,
                                  e.target.value,
                                )
                              }
                              placeholder="0"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pay By Dropdown Section */}
                <div className="filter-section">
                  <Form.Group className="form-group mb-0">
                    <Form.Label className="col-label">Pay By</Form.Label>
                    <Select
                      value={{
                        value: filters.pay_by,
                        label:
                          paybyOptions.find(
                            (opt) => opt.value === filters.pay_by,
                          )?.label || filters.pay_by,
                      }}
                      // onChange={(option) => {
                      //   const selectedValue = option.value;
                      //   setPayBy(selectedValue);
                      //   setFilters((prev) => ({
                      //     ...prev,
                      //     pay_by: selectedValue,
                      //     rate_per: selectedValue,
                      //     page: 1,
                      //   }));
                      // }}
                      onChange={(option) => {
                        const selectedValue = option.value;
                        setPayBy(selectedValue);
                        handleFilterChange("pay_by", selectedValue);
                      }}
                      placeholder="Select Pay By"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      options={paybyOptions}
                      menuPosition="fixed"
                      styles={{
                        menu: (base) => ({
                          ...base,
                          borderRadius: "15px",
                          marginTop: "8px",
                          backgroundColor: "#fff",
                          padding: "6px",
                          zIndex: "1000",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          borderRadius: "8px",
                          padding: "10px",
                          backgroundColor: state.isFocused ? "#F3F9FC" : "#fff",
                          color: "#091F28",
                          cursor: "pointer",
                          "&:active": {
                            backgroundColor: "#F3F9FC",
                          },
                        }),
                      }}
                    />
                  </Form.Group>
                </div>
              </div>
            </Card>
          </div>

          <div className="filter-content">
            <Card className="mb-0">
              <Card.Body>
                <div className="inner-sub-sub-title table-chart">
                  <div className="inner-sub-title">
                    Tax Breakdown by Hospital
                  </div>
                  <div className="toggle-buttons">
                    <button
                      className={view === "table" ? "active" : ""}
                      onClick={() => setView("table")}
                    >
                      📋 Table
                    </button>
                    <button
                      className={view === "chart" ? "active" : ""}
                      onClick={() => setView("chart")}
                    >
                      📊 Chart
                    </button>
                  </div>
                </div>

                {view === "table" ? (
                  <div className="tax-table-wrapper">
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            {allColumns.map((col) => (
                              <th
                                key={col.key}
                                className={`align-top ${col.className || ""}`}
                              >
                                {col.header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {isInitialLoading ? (
                            <tr>
                              <td
                                colSpan={allColumns.length}
                                className="text-center py-5"
                              >
                                <div
                                  className="spinner-border text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                                <div className="mt-2 text-muted">
                                  Loading hospitals...
                                </div>
                              </td>
                            </tr>
                          ) : salariesData.length > 0 ? (
                            salariesData.map((item, i) => {
                              const taxes = item.taxes || {};
                              const deductions = taxes.deductionsMonthly || {};

                              return (
                                <tr key={item.id || i}>
                                  {allColumns.map((col) => {
                                    let display = "0";

                                    if (col.key === "name") {
                                      display =
                                        item.hospital || "Unknown Hospital";
                                    } else if (col.key === "gross") {
                                      display = formatCurrency(
                                        toPayBy(taxes.gross ?? taxes.gross),
                                      );
                                    } else if (col.key === "federal") {
                                      display = formatCurrency(
                                        toPayBy(
                                          taxes.federalTax ??
                                            taxes.federalTaxConverted,
                                        ),
                                      );
                                    } else if (col.key === "state") {
                                      display = formatCurrency(
                                        toPayBy(
                                          taxes.stateTax ??
                                            taxes.stateTaxConverted,
                                        ),
                                      );
                                    } else if (col.key === "ss") {
                                      display = formatCurrency(
                                        toPayBy(
                                          taxes.ssTax ?? taxes.ssTaxConverted,
                                        ),
                                      );
                                    } else if (col.key === "medicare") {
                                      display = formatCurrency(
                                        toPayBy(
                                          taxes.medicareTax ??
                                            taxes.medicareTaxConverted,
                                        ),
                                      );
                                    } else if (col.key === "sdi") {
                                      display = formatCurrency(
                                        toPayBy(
                                          taxes.sdiTax ?? taxes.sdiTaxConverted,
                                        ),
                                      );
                                    } else if (col.key === "net") {
                                      display = formatCurrencyStrong(
                                        toPayBy(
                                          taxes.net ?? taxes.netConverted,
                                        ),
                                      );
                                    } else if (col.key === "postTax") {
                                      const val = taxes.postTax ?? 0;
                                      display =
                                        val > 0 ? formatCurrency(val) : "$0.00";
                                    } else if (col.deductionName) {
                                      const val = deductions[col.deductionName];
                                      display =
                                        val !== undefined && val > 0
                                          ? formatCurrency(val)
                                          : "$0.00";
                                    }

                                    return (
                                      <td
                                        key={col.key}
                                        className={col.className || ""}
                                      >
                                        {display}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={allColumns.length}
                                className="text-center py-5 text-muted"
                              >
                                No hospitals found matching your criteria
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Infinite Scroll Loader */}
                      {loadingMore && (
                        <div className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <div className="mt-2 text-muted">
                            Loading more hospitals...
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="tax-chart-container">
                    {isInitialLoading ? (
                      <div className="text-center py-5">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2 text-muted">Loading hospitals...</p>
                      </div>
                    ) : chartData.data.length === 0 ? (
                      <p className="no-results">
                        No data available for selected criteria
                      </p>
                    ) : (
                      <div className="salary-list">
                        {chartData.data.map((result, index) => {
                          const taxes = result.taxes || {};

                          const netPay =
                            taxes.gross ??
                            taxes.netConverted ??
                            result.grossPay;
                          const displayAmount = toPayBy(netPay);

                          const percentage =
                            chartData.maxSalary > 0
                              ? (result.grossPay / chartData.maxSalary) * 100
                              : 0;

                          return (
                            <div
                              key={result.hospital || index}
                              className="salary-item"
                            >
                              <div className="hospital-name">
                                {result.hospital}
                              </div>
                              <div className="salary-bar-container">
                                <div
                                  className="salary-bar"
                                  style={{
                                    width: `${percentage}%`,
                                  }}
                                ></div>
                              </div>
                              <div className="salary-amount">
                                {formatCurrency(displayAmount)}{" "}
                              </div>
                            </div>
                          );
                        })}

                        <div className="salary-item x-axis">
                          <span className="hospital-name"></span>

                          <div className="salary-bar-container">
                            <span>$0.00</span>
                            <span>
                              ${toPayBy(chartData.niceMax / 2).toLocaleString()}
                            </span>
                            <span>
                              ${toPayBy(chartData.niceMax).toLocaleString()}
                            </span>
                          </div>

                          <span className="salary-amount"></span>
                        </div>

                        {loadingMore && (
                          <p className="text-center mt-3">
                            Loading more hospitals...
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </Row>
      </div>
      
      {/* Subscription Modal */}
      <PaywallSheet
        show={showPaywallSheet}
        onCancel={() => setShowPaywallSheet(false)}
        eventSource={upgradeSource}
      />
    </>
  );
};

export default TaxPage;
